//* Import API configurations
import { api } from "../../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../../constants/consts";

//* Import PaginationData type
import type { Pagination } from "../../../services/stores/types";

//* Import types from types
import type {
  Attribution,
  AttributionDetails,
  AttributionResponse,
} from "../attributions/types/types";

// API methods for Attribution operations
export const attributionAPI = {
  /**
   * Fetch all Attributions with optional search and pagination
   * @param search - Search term
   * @param page - Current page number
   * @param token - Authorization token
   * @param perPage - Items per page
   * @returns Promise containing Attributions and pagination data
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    status: string = "",
    perPage: number = 10
  ): Promise<AttributionResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const statusQuery = status ? `&status=${status}` : "";
    const url = `${API_URI}/admin/private/attributions?page=${page}&perPage=${perPage}${searchQuery}${statusQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response: any) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          attributions: data.attributions || [],
          paginations: pagination,
        };
      })
      .catch((error: any) => {
        console.error("Error fetching attributions:", error);
        throw new Error("Failed to fetch attributions");
      });
  },

  /**
   * Fetch an Attribution by ID
   * @param id - Attribution ID
   * @param token - Authorization token
   * @returns Promise containing Attribution details
   */
  getById: async function (
    id: string,
    token: string
  ): Promise<AttributionDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/attributions/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response: any) => {
        return response.data.data;
      })
      .catch((error: any) => {
        console.error("Error fetching attribution:", error);
        throw new Error("Failed to fetch attribution");
      });
  },

  /**
   * Create a new Attribution (with attachment support)
   * @param data - Attribution data, including optional attachment (File or URL as string)
   * @param token - Authorization token
   * @returns Promise containing the created Attribution
   */
  create: async function (
    data: {
      demand_id: string;
      teacher_id: string;
      discipline_id: string;
      description?: string;
      attachment?: File | string;
      quantity_objective: number;
      quantity_dissertative: number;
      quantity_set: number;
    },
    token: string
  ): Promise<Attribution> {
    const formData = new FormData();
    formData.append("demand_id", data.demand_id);
    formData.append("teacher_id", data.teacher_id);
    formData.append("discipline_id", data.discipline_id);
    if (data.description) {
      formData.append("description", data.description);
    }
    // If attachment is provided, check if it's a File or a link string
    if (data.attachment) {
      if (data.attachment instanceof File) {
        formData.append("attachment", data.attachment);
      } else {
        formData.append("attachment_link", data.attachment);
      }
    }
    formData.append("quantity_objective", data.quantity_objective.toString());
    formData.append(
      "quantity_dissertative",
      data.quantity_dissertative.toString()
    );
    formData.append("quantity_set", data.quantity_set.toString());

    return api
      .request({
        url: `${API_URI}/admin/private/attributions`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      })
      .then((response: any) => response.data.data)
      .catch((error: any) => {
        console.error("Error creating attribution:", error, data);
        throw new Error("Failed to create attribution");
      });
  },

  /**
   * Update an existing Attribution (with attachment support)
   * @param id - Attribution ID
   * @param data - Updated attribution data, including optional attachment (File or URL as string)
   * @param token - Authorization token
   * @returns Promise containing the updated Attribution
   */
  /**
   * Update an existing Attribution (with attachment support)
   * @param id - Attribution ID
   * @param data - Updated attribution data, including optional attachment (File or URL as string)
   * @param token - Authorization token
   * @returns Promise containing the updated Attribution
   */
  update: async function (
    id: string,
    data: {
      demand_id: string;
      teacher_id: string;
      discipline_id: string;
      description?: string;
      attachment?: File | string;
      removeAttachment?: boolean; 
      quantity_objective: number;
      quantity_dissertative: number;
      quantity_set: number;
    },
    token: string
  ): Promise<Attribution> {

    const formData = new FormData();
    formData.append("demand_id", data.demand_id);
    formData.append("teacher_id", data.teacher_id);
    formData.append("discipline_id", data.discipline_id);
    if (data.description) {
      formData.append("description", data.description);
    }

    // Process the attachment
    if (data.attachment) {
      if (data.attachment instanceof File) {
        formData.append("attachment", data.attachment);
      } else {
        formData.append("attachment_link", data.attachment);
      }
    }

    // Check if the attachment should be removed
    formData.append(
      "remove_attachment",
      data.removeAttachment ? "true" : "false"
    );

    formData.append("quantity_objective", data.quantity_objective.toString());
    formData.append(
      "quantity_dissertative",
      data.quantity_dissertative.toString()
    );
    formData.append("quantity_set", data.quantity_set.toString());



    return api
      .request({
        url: `${API_URI}/admin/private/attributions/${id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      })
      .then((response: any) => response.data.data)
      .catch((error: any) => {
        console.error("Error updating attribution:", error);
        throw new Error("Failed to update attribution");
      });
  },

  /**
   * Delete an Attribution by ID
   * @param id - Attribution ID
   * @param token - Authorization token
   * @returns Promise resolving to void
   */
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/attributions/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error: any) => {
        console.error("Error deleting attribution:", error);
        throw new Error("Failed to delete attribution");
      });
  },

  /**
   * Fetch Attributions by Demand ID with search and pagination
   * @param demandId - Demand ID
   * @param token - Authorization token
   * @param search - Search term (optional)
   * @param page - Current page (optional)
   * @param perPage - Items per page (optional)
   * @returns Promise containing Attributions and Pagination data
   */
  getByDemandId: async function (
    demandId: string,
    token: string,
    search: string = "",
    status: string = "",
    page: number = 1,
    perPage: number = 10
  ): Promise<AttributionResponse> {
    if (status === "all") {
      status = "";
    }

    const statusQuery = status ? `&status=${status}` : "";
    const url = `${API_URI}/admin/private/attributions/demands/${demandId}?page=${page}&per_page=${perPage}&search=${search}${statusQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response: any) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          attributions: data.attributions || [],
          paginations: pagination,
        };
      })
      .catch((error: any) => {
        console.error("Error fetching attributions by demand:", error);
        throw new Error("Failed to fetch attributions by demand");
      });
  },

  /**
   * Update the status of an Attribution
   * @param id - Attribution ID
   * @param type - Type of question ("objective", "dissertative", or "set")
   * @param token - Authorization token
   * @returns Promise resolving to void
   */
  updateStatus: async function (
    id: string,
    type: string,
    token: string
  ): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/attributions/${id}/status`,
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { type },
      })
      .then(() => {})
      .catch((error: any) => {
        console.error("Error updating attribution status:", error);
        throw new Error("Failed to update attribution status");
      });
  },
};
